import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import z from "zod";

import { extractPrefixedTeamId } from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import {
  Badge,
  CellWrapper,
  ContextMenu,
  Table,
  TableProps,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { buildDisplayName } from "@smart/itops-utils-basic";

import { NoWebEnquiries } from "./no-web-enquiries";
import type { GqlSubmissionLinkHash, EmbedType } from "../../types";

type WebEnquiryHashRow = {
  label: string;
  staff: string;
  analyticsTracking: boolean;
  submissionLinkHash: GqlSubmissionLinkHash;
};

type SubmissionLinkHashesTableProps = {
  formTitle: string;
  formLinks: GqlSubmissionLinkHash[];
  onEditLink: (submissionLinkHash: GqlSubmissionLinkHash) => void;
  onDeleteLink: (submissionLinkHash: GqlSubmissionLinkHash) => void;
  onCopyLink: (
    submissionLinkHash: GqlSubmissionLinkHash,
    type: EmbedType,
  ) => void;
  onViewQRCode: (submissionLinkHash: GqlSubmissionLinkHash) => void;
};

const WebEnquiryLabelCell = ({
  getValue,
}: CellContext<WebEnquiryHashRow, string>) => (
  <CellWrapper label={getValue()} size="medium" />
);

const WebEnquiryStaffCell = ({
  getValue,
}: CellContext<WebEnquiryHashRow, string>) => (
  <CellWrapper label={getValue()} size="large" />
);

const WebEnquiryAnalyticsCell = ({
  getValue,
}: CellContext<WebEnquiryHashRow, boolean>) => (
  <Badge
    iconLeft="solidCircleSmall"
    label={getValue() ? "Set" : "Not Set"}
    theme="emphasis"
  />
);

const WebEnquiryRowActionsCell =
  ({
    onEditLink,
    onDeleteLink,
  }: Pick<SubmissionLinkHashesTableProps, "onEditLink" | "onDeleteLink">) =>
  ({ row }: CellContext<WebEnquiryHashRow, unknown>) => (
    <div className="flex justify-end">
      <ContextMenu
        id={`web-enquiry-link-row-${row.id}`}
        button={{
          className: "w-[2rem] h-[2rem]",
          variant: "secondarySubtle",
          leftIcon: "solidEllipsis",
        }}
        placement="bottom-end"
        offsetBy={5}
        items={[
          {
            label: "Edit",
            leftIcon: "regularPenToSquare",
            onClick: () => onEditLink(row.original.submissionLinkHash),
          },
          {
            label: "Delete Link",
            leftIcon: "regularTrashCan",
            className: "text-red-550",
            onClick: () => onDeleteLink(row.original.submissionLinkHash),
          },
        ]}
      />
    </div>
  );

const WebEnquiryRowShareCell =
  ({
    onCopyLink,
    onViewQRCode,
  }: Pick<SubmissionLinkHashesTableProps, "onCopyLink" | "onViewQRCode">) =>
  ({ row }: CellContext<WebEnquiryHashRow, unknown>) => (
    <div className="flex justify-end">
      <ContextMenu
        id={`web-enquiry-share-row-${row.id}`}
        button={{
          text: "Share",
          variant: "secondarySubtle",
          size: "small",
          rightIcon: "regularAngleDown",
        }}
        placement="bottom-end"
        offsetBy={5}
        items={[
          {
            label: "Copy Link",
            leftIcon: "regularCopy",
            onClick: () => onCopyLink(row.original.submissionLinkHash, "url"),
          },
          {
            label: "Copy Code: to Embed on Website",
            leftIcon: "regularCopy",
            onClick: () =>
              onCopyLink(row.original.submissionLinkHash, "iframe"),
          },
          {
            label: 'Copy Code: "Contact Us" Button',
            leftIcon: "regularCopy",
            onClick: () =>
              onCopyLink(row.original.submissionLinkHash, "button"),
          },
          {
            label: "View QR Code",
            leftIcon: "regularQrcode",
            onClick: () => onViewQRCode(row.original.submissionLinkHash),
          },
        ]}
      />
    </div>
  );

export const SubmissionLinkHashesTable = ({
  formTitle,
  formLinks,
  onEditLink,
  onDeleteLink,
  onCopyLink,
  onViewQRCode,
}: SubmissionLinkHashesTableProps) => {
  const { staff } = useSmokeballApp();

  const [columnSort, setColumnSort] = useStorage({
    defaultValue: [{ id: "label", desc: true }],
    key: "form-builder-web-enquiry-hashes-table-sort",
    schema: z.array(z.object({ id: z.string(), desc: z.boolean() })),
    storage: "local",
  });

  const tableData = useMemo(
    (): WebEnquiryHashRow[] =>
      formLinks.map((link) => {
        const notifiedIds =
          link.notifiedUserUris?.map(
            (uri) => extractPrefixedTeamId(uri, "sb").id,
          ) || [];
        const notifiedStaff =
          staff?.current?.filter((s) => notifiedIds.includes(s.userId)) || [];
        const staffDisplay = notifiedStaff
          .map((s) => buildDisplayName(s, s.userId))
          .sort((a, b) => a.localeCompare(b))
          .join(", ");

        return {
          label: link.label?.trim() || formTitle || "-",
          staff: staffDisplay,
          analyticsTracking: !!link.analyticsTools?.length,
          submissionLinkHash: link,
        };
      }),
    [formTitle, formLinks, staff?.current],
  );

  const columns = useMemo(() => {
    const c = createColumnHelper<WebEnquiryHashRow>();
    return [
      c.accessor("label", {
        header: "Link Label (Internal)",
        cell: WebEnquiryLabelCell,
      }),
      c.accessor("staff", {
        header: "Staff to Notify Upon Completion",
        cell: WebEnquiryStaffCell,
      }),
      c.accessor("analyticsTracking", {
        header: "Analytics Tracking",
        cell: WebEnquiryAnalyticsCell,
      }),
      c.display({
        id: "share",
        meta: { action: true },
        cell: WebEnquiryRowShareCell({
          onCopyLink,
          onViewQRCode,
        }),
      }),
      c.display({
        id: "actions",
        meta: { action: true },
        cell: WebEnquiryRowActionsCell({ onEditLink, onDeleteLink }),
      }),
    ];
  }, [onEditLink, onDeleteLink, onViewQRCode, onCopyLink]);

  const sort: TableProps<WebEnquiryHashRow>["sort"] = {
    state: columnSort,
    onChange: setColumnSort,
  };

  const select: TableProps<WebEnquiryHashRow>["select"] = {
    state: {},
    onChange: () => {},
  };

  return (
    <div className="rounded rounded-b-none border border-neutral-100 flex-1">
      <Table
        id="form-builder-web-enquiry-links-table"
        columns={columns}
        data={tableData}
        loadingRows={staff?.loading ? 3 : undefined}
        globalFilter={undefined}
        select={select}
        sort={sort}
        className="rounded rounded-b-none"
        empty={<NoWebEnquiries />}
      />
    </div>
  );
};
